import React, { useState, useCallback, useMemo, useRef } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { MoreVertical } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { type LifeSpace } from '../../../hooks/useLifeSpaces'
import { Tooltip, TooltipTrigger, TooltipContent } from '../../ui/tooltip'
import { HBaseIcon } from '../../icons/HBaseIcon'
import './LifeSpaceButton.scss'

export interface LifeSpaceButtonProps {
  space: LifeSpace
  isActive: boolean
  shortcutIndex?: number
  onClick: () => void
  onEdit?: () => void
  onDelete?: () => void
}

const LifeSpaceButtonComponent: React.FC<LifeSpaceButtonProps> = ({
  space,
  isActive,
  shortcutIndex,
  onClick,
  onEdit,
  onDelete
}) => {
  const { t } = useTranslation()
  const [showContextMenu, setShowContextMenu] = useState(false)
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 })
  const pointerStart = useRef<{ x: number; y: number } | null>(null)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: space.id })

  // Memoize style object to prevent recreation
  const style = useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition,
  }), [transform, transition])

  // Memoize context menu handler
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (!space.isCustom) return
    e.preventDefault()
    e.stopPropagation()
    setContextMenuPosition({ x: e.clientX, y: e.clientY })
    setShowContextMenu(true)
  }, [space.isCustom])

  // Memoize edit handler
  const handleEdit = useCallback(() => {
    setShowContextMenu(false)
    onEdit?.()
  }, [onEdit])

  // Memoize delete handler
  const handleDelete = useCallback(() => {
    setShowContextMenu(false)
    onDelete?.()
  }, [onDelete])

  // Memoize close context menu handler
  const handleCloseContextMenu = useCallback(() => setShowContextMenu(false), [])

  // Memoize more button click handler
  const handleMoreClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    handleContextMenu(e)
  }, [handleContextMenu])

  // Click detection: track pointer start, fire click on pointerup if no drag
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    pointerStart.current = { x: e.clientX, y: e.clientY }
    listeners?.onPointerDown?.(e as unknown as PointerEvent)
  }, [listeners])

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!pointerStart.current) return
    const dx = Math.abs(e.clientX - pointerStart.current.x)
    const dy = Math.abs(e.clientY - pointerStart.current.y)
    pointerStart.current = null

    if (dx < 5 && dy < 5 && e.button === 0) {
      onClick()
      // Blur to prevent tooltip from showing on window refocus
      ;(e.currentTarget as HTMLElement)?.blur()
    }
  }, [onClick])

  // Spread dnd-kit listeners but override onPointerDown with our wrapper
  const dndListeners = { ...listeners }
  delete dndListeners.onPointerDown

  // Memoize inner button style to prevent recreation
  const innerStyle = useMemo(() => ({
    backgroundColor: space.isCustom && space.color ? `${space.color}20` : undefined,
    '--space-color': space.color || undefined,
  } as React.CSSProperties), [space.isCustom, space.color])

  const Icon = space.icon

  return (
    <>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              ref={setNodeRef}
              style={style}
              {...attributes}
              {...dndListeners}
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              className={`life-space-button ${isActive ? 'life-space-button--active' : ''}`}
              onContextMenu={handleContextMenu}
            >
        <div
          className="life-space-button__inner"
          style={innerStyle}
        >
          <span className="life-space-button__hbase life-space-button__hbase--hover">
            <HBaseIcon size={32} color="var(--muted)" />
          </span>
          <span className="life-space-button__hbase life-space-button__hbase--pressed">
            <HBaseIcon size={32} color="var(--border)" />
          </span>
          {space.color && (
            <span className="life-space-button__hbase life-space-button__hbase--active">
              <HBaseIcon size={32} color={space.color} />
            </span>
          )}
          <span className="life-space-button__icon">
            <Icon
              size={16}
              color={isActive && space.color ? '#ffffff' : (space.isCustom && space.color ? space.color : undefined)}
            />
          </span>
          {space.badge && space.badge > 0 && (
            <span className="life-space-button__badge">{space.badge}</span>
          )}
        </div>

        {space.isCustom && (
          <button
            className="life-space-button__more"
            onClick={handleMoreClick}
          >
            <MoreVertical size={16} />
          </button>
        )}
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            shortcut={shortcutIndex != null && shortcutIndex <= 9 ? { kind: 'character', text: String(shortcutIndex) } : undefined}
          >
            {space.id === 'all' ? t('lifeSpaces.allBreadcrumb') : space.name}
          </TooltipContent>
        </Tooltip>

      {/* Context Menu */}
      {showContextMenu && (
        <>
          <div
            className="life-space-button__context-backdrop"
            onClick={handleCloseContextMenu}
          />
          <div
            className="life-space-button__context-menu"
            style={{
              position: 'fixed',
              left: `${contextMenuPosition.x}px`,
              top: `${contextMenuPosition.y}px`
            }}
          >
            <button
              className="life-space-button__context-item"
              onClick={handleEdit}
            >
              {t('lifeSpaces.edit')}
            </button>
            <button
              className="life-space-button__context-item life-space-button__context-item--danger"
              onClick={handleDelete}
            >
              {t('lifeSpaces.delete')}
            </button>
          </div>
        </>
      )}
    </>
  )
}

/**
 * Individual Life Space button rendered inside the sidebar rail.
 *
 * @description
 * Displays a squircle (HBase) icon button for a single Life Space.
 * Supports drag-and-drop reordering via dnd-kit, right-click context menu
 * (edit / delete) for custom spaces, and distinguishes click vs. drag
 * via pointer distance tracking.
 *
 * Uses a custom `React.memo` comparator that checks only the relevant
 * subset of `LifeSpace` fields to avoid unnecessary re-renders.
 *
 * @example
 * <LifeSpaceButton
 *   space={space}
 *   isActive={activeSpace === space.id}
 *   onClick={() => onSpaceChange(space.id)}
 *   onEdit={() => openEditDialog(space)}
 *   onDelete={() => confirmDelete(space)}
 * />
 */
export const LifeSpaceButton = React.memo(LifeSpaceButtonComponent, (prevProps, nextProps) => {
  // Custom comparison function for better performance
  return (
    prevProps.isActive === nextProps.isActive &&
    prevProps.shortcutIndex === nextProps.shortcutIndex &&
    prevProps.space.id === nextProps.space.id &&
    prevProps.space.name === nextProps.space.name &&
    prevProps.space.color === nextProps.space.color &&
    prevProps.space.badge === nextProps.space.badge &&
    prevProps.space.isCustom === nextProps.space.isCustom &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.onEdit === nextProps.onEdit &&
    prevProps.onDelete === nextProps.onDelete
  )
})
